// Hook custom per recuperare la lista degli agenti di un'agenzia tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from "react";
import axios from "axios";

// Base URL dell'API presa dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione degli agenti
interface Agent {
  img: string;
  name: string;
  tel: string;
  mobile: string;
  email: string;
}

/**
 * useGetAgentsByAgency
 * @param agencyId - ID dell'agenzia di cui vogliamo ottenere gli agenti
 * @returns { agents, loading, error }
 *
 * Nota per il prossimo dev:
 * - `agents` contiene la lista di agenti tipizzati come Agent[]
 * - `loading` è true mentre la chiamata è in corso
 * - `error` contiene un messaggio di errore in caso di fallimento
 */
export const useGetAgentsByAgency = (agencyId: number) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        // Chiamata POST all'endpoint /agents
        const response = await axios.post(`${API_BASE_URL}/agents`, {
          agencyId,
          limit: 10,
          offset: 0,
          withCount: false,
        });

        // Mappo i dati ricevuti dall'API nella struttura Agent
        const agentData = response.data.map((agent: any) => ({
          img: agent.img,
          name: agent.name + " " + agent.lastName,
          tel: agent.phone1,
          mobile: agent.phone2 || "",
          email: agent.email || "",
        }));

        // salvo nello state
        setAgents(agentData);
      } catch (err) {
        console.error("Errore nel recupero degli agenti:", err);
        setError("Errore nel recupero degli agenti");
      } finally {
        setLoading(false); // fine loading
      }
    };

    fetchAgents();
  }, [agencyId]); // richiamo ogni volta che cambia agencyId

  return { agents, loading, error };
};